<template>
  <top-navigation :title="$t('pages.group.members')" />

  <div class="mx-auto px-4 pt-24 pb-8">
    <!-- Members List -->
    <div class="space-y-3">
      <member-item
        v-for="member in membersSorted"
        :key="member.uuid"
        :uuid="member.uuid"
        :member="member"
        @delete-member="openDeleteModal(member.uuid, member)"
      />
    </div>

    <!-- Empty State -->
    <div
      v-if="membersSorted.length === 0"
      class="flex flex-col items-center justify-center py-16"
    >
      <p class="text-gray-500 dark:text-slate-400">
        {{ $t("settings.no_members") }}
      </p>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <conformation-modal
    :confirm-text="$t('global.confirm')"
    :cancel-text="$t('global.cancel')"
    :loading="status.deleting"
    :visible="openDeleteConformation"
    @cancel="cancelDelete"
    @confirm="deleteMember"
  >
    <p class="my-8 text-center font-medium dark:text-slate-200">
      {{ conformationText }}
    </p>
  </conformation-modal>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import TopNavigation from "@/components/TopNavigation.vue";
import MemberItem from "@/components/MemberItem.vue";
import ConformationModal from "@/components/Modals/ConformationModal.vue";
import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "@/helpers/firebase/firebase";
import { setErrorToast } from "@/helpers/notifications/toast";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import * as Sentry from "@sentry/vue";

type Member = {
  uuid: string;
  name: string;
  [key: string]: any;
};

const { t } = useI18n();
const groupsStore = useGroupsStore();
const userStore = useUserStore();

// State
const status = ref({
  deleting: false,
});

const openDeleteConformation = ref(false);
const uuidToDelete = ref("");
const conformationText = ref("");

// Computed
const membersSorted = computed(() => groupsStore.activeGroupMembersSorted);

const isAdmin = computed(() => groupsStore.isAdmin);

const groupId = computed(() => userStore.activeGroup);

// Methods
function openDeleteModal(uuid: string, member: Member): void {
  openDeleteConformation.value = true;
  uuidToDelete.value = uuid;
  conformationText.value =
    t("settings.are_you_sure_you_want_to_delete_member") +
    " " +
    member.name +
    "?";
}

function cancelDelete(): void {
  openDeleteConformation.value = false;
  uuidToDelete.value = "";
  conformationText.value = "";
}

async function deleteMember(): Promise<void> {
  if (!isAdmin.value) {
    setErrorToast({
      message: t("settings.must_be_admin"),
    });
    return;
  }

  if (!uuidToDelete.value) {
    return;
  }

  try {
    status.value.deleting = true;
    const deleteMemberFunction = httpsCallable(
      firebaseFunctions,
      "deleteMember",
    );
    await deleteMemberFunction({
      uuid: uuidToDelete.value,
      groupId: groupId.value,
    });
    await groupsStore.refreshAllUserGroupsData();
  } catch (e: any) {
    switch (e.message) {
      case "User has not enough permissions":
        setErrorToast({
          message: t("settings.must_be_admin"),
        });
        break;
      case "Cannot delete admin":
        setErrorToast({
          message: t("settings.cannot_delete_admin"),
        });
        break;
      default:
        setErrorToast({
          message: t("settings.delete_member_failed"),
        });
        Sentry.captureException(e);
    }
  } finally {
    status.value.deleting = false;
    openDeleteConformation.value = false;
    uuidToDelete.value = "";
    conformationText.value = "";
  }
}
</script>
